import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { Upload, Sparkles, Trash2, Copy, Instagram, Facebook, FileText, Send, CheckCircle2, AlertTriangle, RotateCw, Calendar, Clock, XCircle, Image as ImageIcon, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import ImagePicker from "../../components/ImagePicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
// Lightweight thumbnail URL — bytes are streamed from the server one image
// at a time (cached on disk + by the browser). Avoids loading a 75 MB
// payload up-front for a healthy 100+ photo library.
const photoThumbUrl = (id, size = 600) => `${BACKEND}/api/public/photos/${id}/image.jpg?size=${size}&crop=1`;

export default function Social() {
  const [photos, setPhotos] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [publishing, setPublishing] = useState(null); // `${id}:${target}`
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [queue, setQueue] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [pickerMode, setPickerMode] = useState(null); // null | "add" | { type: "replace", photoId }
  // Per-photo cache-buster — bumped when we rotate or replace bytes so the
  // browser refetches the new image instead of showing the cached old one.
  const [versions, setVersions] = useState({});
  const bumpVersion = (id) => setVersions((m) => ({ ...m, [id]: Date.now() }));
  const photoUrl = (p) => photoThumbUrl(p.id, 800) + (versions[p.id] ? `&v=${versions[p.id]}` : "");
  const fileRef = useRef();

  // Compact listing — skip the heavy `image_base64` field so a 100+-photo
  // library doesn't push 75 MB through the browser on every page open.
  // Each card uses the public /photos/{id}/image.jpg endpoint instead.
  const load = useCallback(async () => { const { data } = await api.get("/admin/photos?compact=1"); setPhotos(data); }, []);
  const loadQueue = useCallback(async () => { try { const { data } = await api.get("/admin/social/queue"); setQueue(data); } catch { /* queue endpoint optional — silently ignore */ } }, []);
  const loadMeta = useCallback(async () => { try { const { data } = await api.get("/admin/meta/status"); setMeta(data); } catch { /* meta endpoint optional — silently ignore */ } }, []);
  useEffect(() => { load(); loadMeta(); loadQueue(); }, [load, loadMeta, loadQueue]);

  const onUpload = async (files) => {
    for (const f of Array.from(files)) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
        toast.error(`${f.name}: only JPG/PNG/WebP accepted`);
        continue;
      }
      const dataUrl = await fileToB64(f);
      const base64 = dataUrl.split(",")[1];
      try {
        await api.post("/admin/photos", { filename: f.name, mime_type: f.type, image_base64: base64 });
      } catch { toast.error(`Upload failed: ${f.name}`); }
    }
    toast.success("Uploaded");
    await load();
  };

  const generateCaptions = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/photos/${id}/generate-social`);
      toast.success("Captions generated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI failed"); } finally { setBusyId(null); }
  };

  const publish = async (id, target) => {
    setPublishing(`${id}:${target}`);
    try {
      const { data } = await api.post(`/admin/photos/${id}/publish-${target}`);
      toast.success(`Posted to ${target === "fb" ? "Facebook" : "Instagram"} ✓ (${data.post_id || "ok"})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${target.toUpperCase()} publish failed`);
    } finally { setPublishing(null); }
  };

  const remove = async (id) => { if (!confirm("Delete photo?")) return; await api.delete(`/admin/photos/${id}`); load(); };
  const rotate = async (id) => { try { await api.post(`/admin/photos/${id}/rotate`, { degrees: 90 }); bumpVersion(id); await load(); } catch { toast.error("Rotate failed"); } };
  const togglePublish = async (p) => { await api.patch(`/admin/photos/${p.id}`, { published_to_gallery: !p.published_to_gallery }); load(); };
  const copy = (text) => { navigator.clipboard.writeText(text || ""); toast.success("Copied to clipboard"); };

  // Extract base64 + mime from any ImagePicker result. We need raw base64
  // here because the photos PATCH endpoint stores image_base64 in Mongo.
  // Sources we accept (in priority order):
  //   1. Direct upload (image_base64 + mime_type already in hand)
  //   2. data:image/...;base64,... URL — strip the prefix
  //   3. site_key — fetch the original site image's bytes server-side
  //   4. photo_id — copy bytes from the photos collection
  const pickToBase64 = async (val) => {
    if (val.image_base64) return { base64: val.image_base64, mime: val.mime_type, filename: val.filename || "image.jpg" };
    if (val.src && val.src.startsWith("data:")) {
      const [header, body] = val.src.split(",");
      const mime = header.slice(5, header.indexOf(";"));
      return { base64: body, mime, filename: "library-image.jpg" };
    }
    if (val.site_key) {
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/site-images/${val.site_key}.jpg`;
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(resp.statusText);
        const blob = await resp.blob();
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result); r.onerror = rej;
          r.readAsDataURL(blob);
        });
        const [header, body] = dataUrl.split(",");
        const mime = header.slice(5, header.indexOf(";"));
        return { base64: body, mime, filename: `${val.site_key}.jpg` };
      } catch { return null; }
    }
    if (val.photo_id) {
      // Use the per-photo public image URL — small and cached.
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/photos/${val.photo_id}/image.jpg?size=1600&crop=0`;
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(resp.statusText);
        const blob = await resp.blob();
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result); r.onerror = rej;
          r.readAsDataURL(blob);
        });
        const [header, body] = dataUrl.split(",");
        const mime = header.slice(5, header.indexOf(";"));
        return { base64: body, mime, filename: "library.jpg" };
      } catch { return null; }
    }
    return null;
  };

  const handlePicker = async (val) => {
    const mode = pickerMode;
    // Close the picker immediately so the user gets instant feedback —
    // any heavy work (decoding bytes, uploading) happens in the background.
    setPickerMode(null);
    const tId = toast.loading("Adding image…");
    const decoded = await pickToBase64(val);
    if (!decoded) { toast.error("Couldn't read that image", { id: tId }); return; }
    if (mode === "add") {
      try {
        await api.post("/admin/photos", {
          filename: decoded.filename,
          mime_type: decoded.mime,
          image_base64: decoded.base64,
        });
        toast.success("Added from library ✓", { id: tId });
        await load();
      } catch { toast.error("Add failed", { id: tId }); }
    } else if (mode?.type === "replace") {
      try {
        await api.patch(`/admin/photos/${mode.photoId}`, {
          image_base64: decoded.base64,
          mime_type: decoded.mime,
          // Force AI captions to be regenerated for the new image
          ai_processed: false,
          ig_caption: null,
          fb_caption: null,
          blog_snippet: null,
        });
        bumpVersion(mode.photoId);
        toast.success("Image replaced — regenerate AI captions next", { id: tId });
        await load();
      } catch { toast.error("Replace failed", { id: tId }); }
    } else {
      toast.dismiss(tId);
    }
  };

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const scheduleSelected = async (platforms) => {
    if (selected.size === 0) { toast.error("Select at least one photo first"); return; }
    const missing = photos.filter(p => selected.has(p.id) && !p.ai_processed);
    if (missing.length && !confirm(`${missing.length} selected photo(s) have no AI captions yet. Schedule anyway?`)) return;
    setScheduling(true);
    try {
      const { data } = await api.post("/admin/social/schedule", { photo_ids: Array.from(selected), platforms });
      toast.success(`Scheduled ${data.scheduled.length} photo(s) for ${platforms.join(" + ")} — one per day at 10:00 London time.`);
      setSelected(new Set());
      await load(); await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scheduling failed");
    } finally { setScheduling(false); }
  };

  const unschedule = async (photoId, platform) => {
    try { await api.post("/admin/social/unschedule", { photo_ids: [photoId], platform }); toast.success("Removed from queue"); await load(); await loadQueue(); }
    catch { toast.error("Unschedule failed"); }
  };

  return (
    <div data-testid="admin-social-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">photos, gallery & social</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Photos & Social</h1>
          <p className="text-sm text-[#6E6074] mt-1">Upload class photos → AI drafts IG & FB captions → one click to publish to your Facebook Page and Instagram.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} data-testid="social-upload-input" />
          <button onClick={() => fileRef.current?.click()} className="sac-btn-primary" data-testid="social-upload-btn"><Upload size={16} /> Upload photos</button>
          <button onClick={() => setPickerMode("add")} className="sac-btn-secondary" data-testid="social-add-from-library-btn"><ImageIcon size={16} /> Add from library</button>
        </div>
      </div>

      <MetaStatusBanner meta={meta} />

      <QueueBar selected={selected} scheduling={scheduling} onSchedule={scheduleSelected} queue={queue} onUnschedule={unschedule} />

      {photos.length === 0 && (
        <div className="admin-surface p-10 text-center">
          <div className="font-display text-2xl text-[#5D2C8F]">No photos yet.</div>
          <p className="text-sm text-[#6E6074] mt-2">Upload some class snaps to see the magic.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {photos.map((p) => (
          <div key={p.id} className="admin-surface p-4 grid md:grid-cols-5 gap-4" data-testid={`photo-card-${p.id}`}>
            <div className="md:col-span-2">
              <div className="relative">
                <img src={photoUrl(p)} alt={p.alt_text || ""} className="rounded-lg w-full aspect-square object-cover" loading="lazy" />
                <label className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 backdrop-blur text-xs text-[#5D2C8F] cursor-pointer shadow">
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`photo-select-${p.id}`} />
                  select
                </label>
                {(p.scheduled_fb_at || p.scheduled_ig_at) && (
                  <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-[#E8C37A] text-[10px] text-[#3A2A47] shadow flex items-center gap-1">
                    <Clock size={10} /> queued
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-3 text-xs">
                <label className="flex items-center gap-2 text-[#5D2C8F]">
                  <input type="checkbox" checked={p.published_to_gallery} onChange={() => togglePublish(p)} data-testid={`photo-publish-${p.id}`} /> Show in public gallery
                </label>
                <div className="flex items-center gap-3">
                  <button onClick={() => setPickerMode({ type: "replace", photoId: p.id })} className="text-[#A685BD]" title="Replace image (keeps captions until regenerated)" data-testid={`photo-replace-${p.id}`}><RefreshCw size={14} /></button>
                  <button onClick={() => rotate(p.id)} className="text-[#A685BD]" title="Rotate 90°" data-testid={`photo-rotate-${p.id}`}><RotateCw size={14} /></button>
                  <button onClick={() => remove(p.id)} className="text-[#A685BD]" data-testid={`photo-delete-${p.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
            <div className="md:col-span-3 space-y-3">
              {!p.ai_processed ? (
                <button onClick={() => generateCaptions(p.id)} disabled={busyId === p.id} className="sac-btn-primary w-full justify-center" data-testid={`photo-generate-${p.id}`}>
                  <Sparkles size={16} /> {busyId === p.id ? "Analysing…" : "Generate IG / FB captions"}
                </button>
              ) : (
                <>
                  <CaptionBlock
                    icon={<Instagram size={14} />} label="Instagram" text={p.ig_caption}
                    onCopy={() => copy(p.ig_caption)}
                    onPublish={meta?.ig_configured ? () => publish(p.id, "ig") : null}
                    publishBusy={publishing === `${p.id}:ig`}
                    publishedAt={p.ig_published_at}
                    testid={`ig-${p.id}`}
                  />
                  <CaptionBlock
                    icon={<Facebook size={14} />} label="Facebook" text={p.fb_caption}
                    onCopy={() => copy(p.fb_caption)}
                    onPublish={meta?.fb_configured ? () => publish(p.id, "fb") : null}
                    publishBusy={publishing === `${p.id}:fb`}
                    publishedAt={p.fb_published_at}
                    testid={`fb-${p.id}`}
                  />
                  <CaptionBlock icon={<FileText size={14} />} label="Blog snippet" text={p.blog_snippet} onCopy={() => copy(p.blog_snippet)} testid={`blog-${p.id}`} />
                  <button onClick={() => generateCaptions(p.id)} disabled={busyId === p.id} className="text-xs text-[#E8C37A]" data-testid={`photo-regenerate-${p.id}`}>↻ Regenerate</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {pickerMode && (
        <ImagePicker
          open={!!pickerMode}
          onClose={() => setPickerMode(null)}
          onPick={handlePicker}
          title={pickerMode === "add" ? "Add a photo from your library" : "Replace this photo"}
        />
      )}
    </div>
  );
}

function MetaStatusBanner({ meta }) {
  if (!meta) return null;
  const igScopesOk = (meta.scopes || []).some(s => s.includes("instagram_content_publish"));
  return (
    <div className="admin-surface p-4 mb-5 bg-[#8EC9C7]/10 border-[#8EC9C7]/40 text-sm" data-testid="meta-status-banner">
      <div className="grid md:grid-cols-2 gap-3">
        <div className="flex items-start gap-2 text-[#3A2A47]">
          {meta.fb_configured ? <CheckCircle2 size={16} className="text-green-700 mt-0.5" /> : <AlertTriangle size={16} className="text-amber-600 mt-0.5" />}
          <div>
            <strong>Facebook:</strong> {meta.fb_configured ? (meta.page_name || "Connected") : "Not configured"}
            {meta.fb_page_id && <span className="text-[#6E6074]"> · Page ID {meta.fb_page_id}</span>}
          </div>
        </div>
        <div className="flex items-start gap-2 text-[#3A2A47]">
          {meta.ig_configured && igScopesOk ? <CheckCircle2 size={16} className="text-green-700 mt-0.5" /> : <AlertTriangle size={16} className="text-amber-600 mt-0.5" />}
          <div>
            <strong>Instagram:</strong> {!meta.ig_configured ? "Not configured" : igScopesOk ? (meta.ig_username ? `@${meta.ig_username}` : "Connected") : "Token missing scopes"}
            {!igScopesOk && meta.ig_configured && (
              <div className="text-xs text-[#6E6074] mt-1">
                Regenerate your Meta User Token with <code className="font-mono">instagram_basic</code> and <code className="font-mono">instagram_content_publish</code> ticked, then paste into <code className="font-mono">META_USER_ACCESS_TOKEN</code> in the backend <code className="font-mono">.env</code>.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CaptionBlock({ icon, label, text, onCopy, onPublish, publishBusy, publishedAt, testid }) {
  return (
    <div className="border border-[#E5E0D8] rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs text-[#5D2C8F] flex items-center gap-1">{icon} {label}</div>
        <div className="flex items-center gap-3">
          {publishedAt && <span className="text-[10px] text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={10} /> Posted</span>}
          {onPublish && (
            <button onClick={onPublish} disabled={publishBusy} className="text-xs text-[#5D2C8F] inline-flex items-center gap-1 disabled:opacity-50" data-testid={`publish-${testid}`}>
              <Send size={12} /> {publishBusy ? "Posting…" : "Publish"}
            </button>
          )}
          <button onClick={onCopy} className="text-xs text-[#E8C37A] inline-flex items-center gap-1" data-testid={`copy-${testid}`}><Copy size={12} /> Copy</button>
        </div>
      </div>
      <div className="text-xs text-[#3A2A47] whitespace-pre-wrap leading-relaxed max-h-40 overflow-auto">{text || <span className="text-[#6E6074]">—</span>}</div>
    </div>
  );
}

function fileToB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
}


function QueueBar({ selected, scheduling, onSchedule, queue, onUnschedule }) {
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/London", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <div className="admin-surface p-5 mb-5" data-testid="queue-bar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="font-display text-xl text-[#5D2C8F] flex items-center gap-2"><Calendar size={16} /> Scheduled queue</div>
          <div className="text-xs text-[#6E6074] mt-1">Tick photos below then queue them — the scheduler posts <strong>one per day at 10:00 London time</strong> across FB / IG. Already-posted photos are skipped.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-[#5D2C8F] self-center mr-1" data-testid="queue-selected-count">{selected.size} selected</span>
          <button onClick={() => onSchedule(["fb"])} disabled={scheduling} className="sac-btn-secondary text-xs" data-testid="queue-schedule-fb"><Facebook size={12} /> Queue to FB</button>
          <button onClick={() => onSchedule(["ig"])} disabled={scheduling} className="sac-btn-secondary text-xs" data-testid="queue-schedule-ig"><Instagram size={12} /> Queue to IG</button>
          <button onClick={() => onSchedule(["fb","ig"])} disabled={scheduling} className="sac-btn-primary text-xs" data-testid="queue-schedule-both"><Send size={12} /> Queue to both</button>
        </div>
      </div>
      {queue.length === 0 ? (
        <div className="text-xs text-[#6E6074]">Queue is empty. Tick photos then click a Queue button above.</div>
      ) : (
        <div className="text-xs">
          <div className="grid grid-cols-6 gap-2 font-semibold text-[#5D2C8F] border-b border-[#E5E0D8] pb-2 mb-2">
            <div className="col-span-2">Photo</div><div>FB posts at</div><div>IG posts at</div><div>Status</div><div></div>
          </div>
          {queue.slice(0, 12).map((q) => (
            <div key={q.id} className="grid grid-cols-6 gap-2 items-center py-1.5 border-b border-[#F4EDDD]" data-testid={`queue-row-${q.id}`}>
              <div className="col-span-2 truncate text-[#3A2A47]">{q.filename || q.id.slice(0,8)} <span className="text-[#6E6074]">({q.alt_text || "—"})</span></div>
              <div className="text-[#3A2A47]">{fmtDate(q.scheduled_fb_at)}</div>
              <div className="text-[#3A2A47]">{fmtDate(q.scheduled_ig_at)}</div>
              <div>{q.auto_post_status === "failed" ? <span className="text-red-600">failed</span> : <span className="text-[#5D2C8F]">queued</span>}</div>
              <div className="flex gap-2 justify-end">
                {q.scheduled_fb_at && <button onClick={() => onUnschedule(q.id, "fb")} className="text-[#A685BD] text-xs inline-flex items-center gap-1" data-testid={`queue-cancel-fb-${q.id}`}><XCircle size={12} /> FB</button>}
                {q.scheduled_ig_at && <button onClick={() => onUnschedule(q.id, "ig")} className="text-[#A685BD] text-xs inline-flex items-center gap-1" data-testid={`queue-cancel-ig-${q.id}`}><XCircle size={12} /> IG</button>}
              </div>
            </div>
          ))}
          {queue.length > 12 && <div className="text-[#6E6074] text-xs pt-2">…and {queue.length - 12} more queued.</div>}
        </div>
      )}
    </div>
  );
}
